// ─── StockItemsView — catálogo de materiais (Petrotec Angola) ─────────────
//
// Catálogo central por tenant. O operador gere identidade/metadados dos
// materiais (refª, nome, unidade, categoria, preço, min stock). As
// quantidades NÃO se editam aqui — só via movimentos (reabastecimento,
// transferência). Para essa parte existe o ecrã StockByVehicle.

import React, { useState, useEffect, useContext, useCallback } from 'react';
import { useFormCardScroll, getTenantCurrency } from '../../utils/manageHelpers';
import {
  Box, Typography, Button, TextField, IconButton, Tooltip, Chip, Switch,
  FormControlLabel, CircularProgress, Select, MenuItem, FormControl, InputLabel,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import AddIcon from '@material-ui/icons/Add';
import EditIcon from '@material-ui/icons/Edit';
import DeleteIcon from '@material-ui/icons/Delete';
import CheckIcon from '@material-ui/icons/Check';
import CloseIcon from '@material-ui/icons/Close';
import RefreshIcon from '@material-ui/icons/Refresh';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import Inventory2Icon from '@material-ui/icons/Category';
import LocalShippingIcon from '@material-ui/icons/LocalShipping';
import { useDispatch } from 'react-redux';
import { setActiveView } from '../../store/slices/uiSlice';
import { AuthContext } from '../../App';
import ConfirmDialog from '../common/ConfirmDialog';

const useStyles = makeStyles(() => ({
  root: {
    width: '100%', height: '100%', display: 'flex', flexDirection: 'column',
    background: '#f8fafc', overflow: 'hidden',
  },
  header: {
    display: 'flex', alignItems: 'center', gap: 12, padding: '16px 24px',
    background: '#fff',
    borderBottom: '1px solid #e2e8f0',
  },
  titleBox: { flex: 1, minWidth: 0 },
  breadcrumb: {
    fontSize: '0.68rem', color: '#64748b', fontWeight: 600,
    display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2, flexWrap: 'wrap',
  },
  crumbLink: { cursor: 'pointer', color: '#dc2626', '&:hover': { textDecoration: 'underline' } },
  title: {
    fontSize: '1.05rem', fontWeight: 800, color: '#0f172a',
    display: 'flex', alignItems: 'center', gap: 8,
  },
  toolbar: {
    display: 'flex', gap: 8, padding: '12px 24px', alignItems: 'center',
    borderBottom: '1px solid #f1f5f9', background: '#fff', flexWrap: 'wrap',
  },
  searchInput: {
    flex: 1, maxWidth: 360,
    '& .MuiOutlinedInput-root': { borderRadius: 8, background: '#fff', fontSize: '0.78rem' },
  },
  addButton: {
    background: '#3b82f6', color: '#fff', borderRadius: 8,
    textTransform: 'none', fontWeight: 700, padding: '6px 16px',
    fontSize: '0.74rem', boxShadow: 'none',
    '&:hover': { background: '#2563eb', boxShadow: '0 2px 6px rgba(59,130,246,0.25)' },
  },
  saveButton: {
    background: '#3b82f6', color: '#fff',
    textTransform: 'none', fontSize: '0.78rem', fontWeight: 700,
    padding: '6px 18px', boxShadow: 'none',
    '&:hover': { background: '#2563eb' },
  },
  list: { flex: 1, overflowY: 'auto', padding: '16px 24px' },
  card: {
    background: '#fff', borderRadius: 12, padding: '14px 18px', marginBottom: 10,
    border: '1px solid #e2e8f0',
    display: 'flex', alignItems: 'center', gap: 14,
    transition: 'all 0.2s',
    '&:hover': { boxShadow: '0 4px 12px rgba(0,0,0,0.06)', borderColor: '#cbd5e1' },
  },
  cardInactive: { opacity: 0.55, background: '#f8fafc' },
  cardIcon: {
    width: 40, height: 40, borderRadius: 10, display: 'flex',
    alignItems: 'center', justifyContent: 'center',
    background: '#dbeafe',
  },
  cardMain: { flex: 1, minWidth: 0 },
  cardName: {
    fontSize: '0.92rem', fontWeight: 700, color: '#0f172a',
    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
  },
  cardMeta: {
    fontSize: '0.7rem', color: '#64748b', marginTop: 4,
    display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center',
  },
  cardActions: { display: 'flex', gap: 4, alignItems: 'center' },
  stockPill: {
    display: 'inline-flex', alignItems: 'center', gap: 4,
    padding: '4px 8px', borderRadius: 999,
    fontSize: '0.7rem', fontWeight: 800,
  },
  empty: {
    padding: '80px 24px', textAlign: 'center', color: '#94a3b8',
    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 14,
  },
  formCard: {
    background: '#fff', borderRadius: 12, padding: 20, marginBottom: 16,
    border: '1px solid #e2e8f0',
    boxShadow: '0 4px 16px rgba(0,0,0,0.05)',
    display: 'flex', flexDirection: 'column', gap: 14,
  },
  formTitle: {
    fontSize: '0.86rem', fontWeight: 700, color: '#0f172a',
    display: 'flex', alignItems: 'center', gap: 8,
  },
  formRow2: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 },
  formRow3: { display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 },
  formActions: { display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 4 },
}));

const UNITS = [
  { value: 'un', label: 'Unidade (un)' },
  { value: 'kg', label: 'Quilograma (kg)' },
  { value: 'l',  label: 'Litro (l)' },
  { value: 'm',  label: 'Metro (m)' },
  { value: 'caixa', label: 'Caixa' },
];

const EMPTY_FORM = {
  code: '', name: '', description: '', unit: 'un',
  category: '', minStock: '', unitPrice: '',
};

function StockItemsView({ lang = 'pt' }) {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { token, clientSettings } = useContext(AuthContext);
  const tenantCurrency = getTenantCurrency(clientSettings);
  const pt = lang === 'pt';

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [showInactive, setShowInactive] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const formCardRef = useFormCardScroll(editingId);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  const fetchAll = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setError(null);
    try {
      const qs = showInactive ? '?includeInactive=1' : '';
      const r = await fetch(`/planner-api/stock-items${qs}`, { headers });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const data = await r.json();
      setItems(Array.isArray(data) ? data : []);
    } catch (e) {
      setError(pt ? 'Não foi possível carregar.' : 'Could not load.');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, showInactive, pt]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  function startCreate() { setForm(EMPTY_FORM); setEditingId('new'); }
  function startEdit(it) {
    setForm({
      code: it.code,
      name: it.name,
      description: it.description || '',
      unit: it.unit || 'un',
      category: it.category || '',
      minStock: it.minStock != null ? String(it.minStock) : '',
      unitPrice: it.unitPrice != null ? String(it.unitPrice) : '',
    });
    setEditingId(it.id);
  }
  function cancelEdit() { setEditingId(null); setForm(EMPTY_FORM); }

  async function saveForm() {
    if (!form.code.trim()) {
      setError(pt ? 'Código obrigatório.' : 'Code required.'); return;
    }
    if (!form.name.trim() || form.name.trim().length < 2) {
      setError(pt ? 'Nome obrigatório.' : 'Name required.'); return;
    }
    setSaving(true);
    setError(null);
    try {
      const isNew = editingId === 'new';
      const body = JSON.stringify({
        // Em edição, o code não muda (não vai no body)
        ...(isNew ? { code: form.code.trim() } : {}),
        name: form.name.trim(),
        description: form.description.trim() || null,
        unit: form.unit,
        category: form.category.trim() || null,
        minStock: form.minStock === '' ? null : Number(form.minStock),
        unitPrice: form.unitPrice === '' ? null : Number(form.unitPrice),
      });
      const url = isNew ? '/planner-api/stock-items' : `/planner-api/stock-items/${editingId}`;
      const method = isNew ? 'POST' : 'PUT';
      const r = await fetch(url, { method, headers, body });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.error || `HTTP ${r.status}`);
      }
      setEditingId(null);
      setForm(EMPTY_FORM);
      await fetchAll();
    } catch (e) {
      setError(e.message || (pt ? 'Erro a guardar.' : 'Save error.'));
    } finally {
      setSaving(false);
    }
  }

  async function toggleActive(it) {
    try {
      const r = await fetch(`/planner-api/stock-items/${it.id}`, {
        method: 'PUT', headers,
        body: JSON.stringify({ isActive: !it.isActive }),
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      await fetchAll();
    } catch (e) { setError(e.message); }
  }

  async function confirmDeleteNow() {
    if (!deleteConfirm) return;
    try {
      const r = await fetch(`/planner-api/stock-items/${deleteConfirm.id}`, {
        method: 'DELETE', headers,
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      setDeleteConfirm(null);
      await fetchAll();
    } catch (e) { setError(e.message); setDeleteConfirm(null); }
  }

  const filtered = items.filter(it => {
    if (!search.trim()) return true;
    const q = search.trim().toLowerCase();
    return [it.code, it.name, it.description, it.category].some(v =>
      v && String(v).toLowerCase().includes(q));
  });

  return (
    <Box className={classes.root}>
      <Box className={classes.header}>
        <Tooltip title={pt ? 'Voltar a Manutenção' : 'Back to Maintenance'}>
          <IconButton size="small" onClick={() => dispatch(setActiveView('maintenance'))}>
            <ArrowBackIcon style={{ fontSize: 20 }} />
          </IconButton>
        </Tooltip>
        <Box className={classes.titleBox}>
          <Typography className={classes.title}>
            <Inventory2Icon style={{ fontSize: 20, color: '#3b82f6' }} />
            {pt ? 'Materiais' : 'Materials'}
            <span style={{ marginLeft: 10, padding: '2px 8px', background: '#f0f9ff', color: '#0369a1', border: '1px solid #e0f2fe', borderRadius: 6, fontSize: '0.7rem', fontWeight: 700 }}>
              {tenantCurrency.symbol}
            </span>
          </Typography>
          <Typography style={{ fontSize: '0.7rem', color: '#64748b', marginTop: 2 }}>
            {pt
              ? 'Catálogo central de peças/consumíveis. Para reabastecer viaturas vai a "Stock por viatura".'
              : 'Central catalogue. To replenish vehicles use "Stock by vehicle".'}
          </Typography>
        </Box>
        <Tooltip title={pt ? 'Stock por viatura' : 'Stock by vehicle'}>
          <Button
            size="small" startIcon={<LocalShippingIcon style={{ fontSize: 16 }} />}
            onClick={() => dispatch(setActiveView('stockByVehicle'))}
            style={{
              textTransform: 'none', fontSize: '0.74rem', fontWeight: 700,
              color: '#475569', background: '#fff', border: '1px solid #e2e8f0',
              borderRadius: 8, padding: '5px 14px',
            }}
          >
            {pt ? 'Stock por viatura' : 'Stock by vehicle'}
          </Button>
        </Tooltip>
        <Tooltip title={pt ? 'Recarregar' : 'Refresh'}>
          <IconButton size="small" onClick={fetchAll} disabled={loading}>
            <RefreshIcon style={{ fontSize: 18 }} />
          </IconButton>
        </Tooltip>
      </Box>

      <Box className={classes.toolbar}>
        <TextField className={classes.searchInput}
          size="small" variant="outlined"
          placeholder={pt ? 'Procurar por código, nome, categoria…' : 'Search…'}
          value={search} onChange={e => setSearch(e.target.value)} />
        <FormControlLabel
          control={<Switch size="small" checked={showInactive}
            onChange={e => setShowInactive(e.target.checked)} />}
          label={<Typography style={{ fontSize: '0.75rem', color: '#475569' }}>
            {pt ? 'Mostrar desactivados' : 'Show inactive'}
          </Typography>} />
        <Box style={{ flex: 1 }} />
        <Button className={classes.addButton} startIcon={<AddIcon style={{ fontSize: 18 }} />}
          onClick={startCreate} disabled={editingId !== null}>
          {pt ? 'Novo material' : 'New material'}
        </Button>
      </Box>

      <Box className={classes.list}>
        {error && (
          <Box style={{
            padding: '10px 14px', background: '#fef2f2',
            border: '1px solid #fecaca', color: '#991b1b',
            borderRadius: 8, marginBottom: 12, fontSize: '0.78rem',
          }}>{error}</Box>
        )}

        {editingId !== null && (
          <Box ref={formCardRef} className={classes.formCard}>
            <Typography className={classes.formTitle}>
              <Inventory2Icon style={{ fontSize: 18, color: '#3b82f6' }} />
              {editingId === 'new'
                ? (pt ? 'Novo material' : 'New material')
                : (pt ? 'Editar material' : 'Edit material')}
            </Typography>
            <Box className={classes.formRow2}>
              <TextField label={pt ? 'Código *' : 'Code *'}
                size="small" variant="outlined" value={form.code}
                onChange={e => setForm({ ...form, code: e.target.value })}
                disabled={editingId !== 'new'} autoFocus />
              <TextField label={pt ? 'Nome *' : 'Name *'}
                size="small" variant="outlined" value={form.name}
                onChange={e => setForm({ ...form, name: e.target.value })} />
            </Box>
            <TextField label={pt ? 'Descrição' : 'Description'}
              size="small" variant="outlined" fullWidth multiline minRows={2}
              value={form.description}
              onChange={e => setForm({ ...form, description: e.target.value })} />
            <Box className={classes.formRow3}>
              <FormControl size="small" variant="outlined">
                <InputLabel>{pt ? 'Unidade' : 'Unit'}</InputLabel>
                <Select label={pt ? 'Unidade' : 'Unit'} value={form.unit}
                  onChange={e => setForm({ ...form, unit: e.target.value })}>
                  {UNITS.map(u => <MenuItem key={u.value} value={u.value}>{u.label}</MenuItem>)}
                </Select>
              </FormControl>
              <TextField label={pt ? 'Categoria' : 'Category'}
                size="small" variant="outlined" value={form.category}
                onChange={e => setForm({ ...form, category: e.target.value })}
                placeholder="filtros, mangueiras…" />
              <TextField label={pt ? 'Min. stock (alerta)' : 'Min. stock'}
                size="small" variant="outlined" type="number" value={form.minStock}
                onChange={e => setForm({ ...form, minStock: e.target.value })} />
            </Box>
            <TextField label={pt ? `Preço unitário em ${tenantCurrency.code} (opcional)` : `Unit price in ${tenantCurrency.code} (optional)`}
              size="small" variant="outlined" type="number"
              value={form.unitPrice}
              onChange={e => setForm({ ...form, unitPrice: e.target.value })}
              InputProps={{ endAdornment: <span style={{ fontSize: '0.74rem', color: '#94a3b8', fontWeight: 600 }}>{tenantCurrency.symbol}</span> }}
              fullWidth />
            <Box className={classes.formActions}>
              <Button onClick={cancelEdit} disabled={saving}
                style={{ textTransform: 'none', fontSize: '0.78rem' }}>
                {pt ? 'Cancelar' : 'Cancel'}
              </Button>
              <Button onClick={saveForm} disabled={saving} className={classes.saveButton}
                startIcon={saving ? <CircularProgress size={14} style={{ color: '#fff' }} /> : <CheckIcon style={{ fontSize: 16 }} />}>
                {pt ? 'Guardar' : 'Save'}
              </Button>
            </Box>
          </Box>
        )}

        {loading && items.length === 0 ? (
          <Box className={classes.empty}>
            <CircularProgress size={32} style={{ color: '#3b82f6' }} />
            <Typography style={{ fontSize: '0.8rem' }}>{pt ? 'A carregar…' : 'Loading…'}</Typography>
          </Box>
        ) : filtered.length === 0 ? (
          <Box className={classes.empty}>
            <Inventory2Icon style={{ fontSize: 48, color: '#cbd5e1' }} />
            <Typography style={{ fontSize: '0.85rem', fontWeight: 600 }}>
              {search ? (pt ? 'Sem resultados.' : 'No results.') : (pt ? 'Ainda não há materiais.' : 'No materials yet.')}
            </Typography>
            {!search && (
              <Button className={classes.addButton} startIcon={<AddIcon style={{ fontSize: 18 }} />}
                onClick={startCreate}>
                {pt ? 'Criar primeiro' : 'Create first one'}
              </Button>
            )}
          </Box>
        ) : (
          filtered.map(it => {
            const lowStock = it.minStock != null && it.centralStock <= it.minStock;
            return (
              <Box key={it.id} className={`${classes.card} ${!it.isActive ? classes.cardInactive : ''}`}>
                <Box className={classes.cardIcon}>
                  <Inventory2Icon style={{ fontSize: 20, color: '#3b82f6' }} />
                </Box>
                <Box className={classes.cardMain}>
                  <Box style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    <Typography className={classes.cardName}>{it.name}</Typography>
                    <Chip label={it.code} size="small"
                      style={{ height: 18, fontSize: '0.6rem', background: '#e2e8f0', color: '#475569', fontWeight: 700 }} />
                    {it.category && (
                      <Chip label={it.category} size="small"
                        style={{ height: 18, fontSize: '0.6rem', background: '#dbeafe', color: '#1e40af', fontWeight: 700 }} />
                    )}
                    {!it.isActive && (
                      <Chip label={pt ? 'Desactivado' : 'Inactive'} size="small"
                        style={{ height: 18, fontSize: '0.6rem', background: '#e2e8f0', color: '#475569' }} />
                    )}
                  </Box>
                  <Box className={classes.cardMeta}>
                    <span>{pt ? 'Unidade' : 'Unit'}: <b>{it.unit}</b></span>
                    {it.unitPrice != null && <span>{pt ? 'Preço' : 'Price'}: {it.unitPrice} {tenantCurrency.symbol}/{it.unit}</span>}
                    {it.minStock != null && <span>{pt ? 'Mín.' : 'Min.'}: {it.minStock}</span>}
                  </Box>
                </Box>
                <Box className={classes.stockPill}
                  style={{
                    background: lowStock ? '#fee2e2' : '#dcfce7',
                    color: lowStock ? '#991b1b' : '#166534',
                  }}>
                  {pt ? 'Central' : 'Central'}: {it.centralStock} {it.unit}
                </Box>
                <Box className={classes.stockPill}
                  style={{ background: '#f1f5f9', color: '#475569' }}>
                  {pt ? 'Total' : 'Total'}: {it.totalStock} {it.unit}
                </Box>
                <Box className={classes.cardActions}>
                  <Tooltip title={it.isActive ? (pt ? 'Desactivar' : 'Deactivate') : (pt ? 'Reactivar' : 'Reactivate')}>
                    <IconButton size="small" onClick={() => toggleActive(it)}>
                      {it.isActive
                        ? <CloseIcon style={{ fontSize: 18, color: '#94a3b8' }} />
                        : <CheckIcon style={{ fontSize: 18, color: '#22c55e' }} />}
                    </IconButton>
                  </Tooltip>
                  <Tooltip title={pt ? 'Editar' : 'Edit'}>
                    <IconButton size="small" onClick={() => startEdit(it)} disabled={editingId !== null}>
                      <EditIcon style={{ fontSize: 16, color: '#3b82f6' }} />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title={pt ? 'Eliminar (soft)' : 'Delete (soft)'}>
                    <IconButton size="small" onClick={() => setDeleteConfirm({ id: it.id, name: it.name })}>
                      <DeleteIcon style={{ fontSize: 16, color: '#ef4444' }} />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>
            );
          })
        )}
      </Box>

      <ConfirmDialog
        open={!!deleteConfirm}
        title={pt ? 'Desactivar material?' : 'Deactivate material?'}
        message={deleteConfirm
          ? (pt
            ? `"${deleteConfirm.name}" passa a desactivado. Movimentos antigos mantêm-se. Pode ser reactivado.`
            : `"${deleteConfirm.name}" will be deactivated. Old movements stay.`)
          : ''}
        severity="warning"
        confirmLabel={pt ? 'Desactivar' : 'Deactivate'}
        cancelLabel={pt ? 'Cancelar' : 'Cancel'}
        onConfirm={confirmDeleteNow}
        onClose={() => setDeleteConfirm(null)}
      />
    </Box>
  );
}

export default StockItemsView;
